import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Shield, Users, Sparkles } from "lucide-react";

// ── Friendly error messages ───────────────────────────────────────────────────

const ERROR_MAP = {
  EMAIL_ALREADY_EXISTS:    "An account with this email already exists.",
  EMAIL_ALREADY_REGISTERED:"An account with this email already exists.",
  INVALID_EMAIL:           "Please enter a valid email address.",
  WEAK_PASSWORD:           "Your password is too weak. Please choose a stronger one.",
  REGISTRATION_FAILED:     "Registration failed. Please try again.",
};

function friendlyError(detail) {
  if (!detail) return "Registration failed. Please try again.";
  if (typeof detail !== "string") return "Registration failed. Please try again.";
  return ERROR_MAP[detail] || detail;
}

// ── Password strength ─────────────────────────────────────────────────────────

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: "", color: "" };
  let score = 0;
  if (password.length >= 6)  score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: "Weak",   color: "bg-red-500" };
  if (score <= 3) return { score: 2, label: "Medium",  color: "bg-amber-400" };
  return           { score: 3, label: "Strong",  color: "bg-green-500" };
}

// ── Trust perks shown on left panel ──────────────────────────────────────────

const PERKS = [
  { icon: Users,    text: "Join 2,000+ women who wear Rivora every day" },
  { icon: Sparkles, text: "Early access to new collections & offers" },
  { icon: Shield,   text: "Secure account with 92.5 certified jewellery" },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate     = useNavigate();

  const [formData, setFormData] = useState({
    name:            "",
    email:           "",
    phone:           "",
    password:        "",
    confirmPassword: "",
  });
  const [showPass, setShowPass]           = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [loading, setLoading]             = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const strength      = useMemo(() => getPasswordStrength(formData.password), [formData.password]);
  const passwordMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;

  const update = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.phone);
      navigate("/account");
      toast.success("Welcome to Rivora! 🎉");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(friendlyError(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT — image panel (desktop only) ────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1625491263326-a3d3ac97e523?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwc2lsdmVyJTIwYnJhY2VsZXQlMjBwZW5kYW50fGVufDB8fHx8MTc3NjA3ODAzNHww&ixlib=rb-4.1.0&q=85"
          alt="Rivora silver jewellery"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/88 via-zinc-900/30 to-zinc-900/35" />

        {/* Brand name top-left */}
        <div className="relative z-10 p-10">
          <div className="cormorant text-2xl font-light tracking-widest text-white opacity-90">
            RIVORA <span className="text-[#D4AF37]">JEWELS</span>
          </div>
        </div>

        {/* Bottom — quote + perks */}
        <div className="relative z-10 mt-auto p-10 pb-14">
          <div className="w-10 h-0.5 bg-[#D4AF37] mb-6" aria-hidden="true" />
          <blockquote className="cormorant text-3xl sm:text-4xl font-light italic text-white leading-snug mb-8">
            "Start your silver<br />story today."
          </blockquote>

          {/* Perks list */}
          <div className="space-y-3">
            {PERKS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-3">
                <Icon size={14} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-white/70 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT — form panel ────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col bg-[#FAFAFA]">

        {/* Mobile — gold top accent bar */}
        <div className="lg:hidden w-full h-1 bg-[#D4AF37]" aria-hidden="true" />

        {/* Mobile — top nav */}
        <div className="lg:hidden flex items-center px-6 pt-5">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 uppercase tracking-widest transition-colors"
          >
            <ArrowLeft size={13} />
            Back to Shop
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">

            {/* Mobile — brand name */}
            <Link to="/" className="inline-block lg:hidden mb-8">
              <div className="cormorant text-2xl font-light tracking-widest text-zinc-900">
                RIVORA <span className="text-[#D4AF37]">JEWELS</span>
              </div>
            </Link>

            {/* Desktop — back link */}
            <Link
              to="/"
              className="hidden lg:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 uppercase tracking-widest transition-colors mb-8"
            >
              <ArrowLeft size={12} />
              Back to Shop
            </Link>

            {/* Heading */}
            <h2
              className="cormorant text-4xl font-light text-zinc-900 mt-2 mb-1"
              data-testid="register-title"
            >
              Create Account
            </h2>
            <p className="text-sm text-zinc-500 mb-8">
              Join Rivora and discover everyday elegance.
            </p>

            {/* ── Form ────────────────────────────────────────────────── */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">

              {/* ── Group 1: Identity ─────────────────────────────────── */}
              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={update("name")}
                    className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="Your full name"
                    required
                    data-testid="name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={update("email")}
                    className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>

                {/* Phone — clearly labelled as optional */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Phone Number{" "}
                    <span className="normal-case tracking-normal text-zinc-400">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={update("phone")}
                    className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* Subtle divider between identity and security fields */}
              <div className="pt-1 pb-1">
                <div className="w-full border-t border-zinc-100" aria-hidden="true" />
              </div>

              {/* ── Group 2: Security ─────────────────────────────────── */}
              <div className="space-y-4">
                {/* Password + toggle */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => {
                        update("password")(e);
                        setPasswordTouched(true);
                      }}
                      className="w-full border border-zinc-300 bg-white px-4 py-3 pr-12 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                      placeholder="Min. 6 characters"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors p-1"
                      aria-label={showPass ? "Hide password" : "Show password"}
                      data-testid="toggle-password"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {/* Password strength bar */}
                  {passwordTouched && formData.password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3].map((bar) => (
                          <div
                            key={bar}
                            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                              bar <= strength.score ? strength.color : "bg-zinc-200"
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-xs ${
                        strength.score === 1 ? "text-red-500" :
                        strength.score === 2 ? "text-amber-500" : "text-green-600"
                      }`}>
                        {strength.label} password
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={update("confirmPassword")}
                      className={`w-full border bg-white px-4 py-3 pr-12 text-sm focus:outline-none transition-colors ${
                        !passwordMatch
                          ? "border-red-400 focus:border-red-500"
                          : "border-zinc-300 focus:border-zinc-900"
                      }`}
                      placeholder="Re-enter your password"
                      required
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors p-1"
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {/* Mismatch warning */}
                  {!passwordMatch && formData.confirmPassword.length > 0 && (
                    <p className="text-xs text-red-500 mt-1.5">Passwords do not match</p>
                  )}
                </div>
              </div>

              {/* Terms notice */}
              <p className="text-xs text-zinc-400 leading-relaxed pt-1">
                By creating an account you agree to our{" "}
                <Link to="/policies" className="text-zinc-600 hover:underline underline-offset-2">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/policies" className="text-zinc-600 hover:underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !passwordMatch}
                className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-3.5 uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? "Creating account…" : "Create Account"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#FAFAFA] px-3 text-zinc-400 tracking-widest">Or</span>
              </div>
            </div>

            {/* Google */}
            <button
              onClick={handleGoogleAuth}
              className="w-full border border-zinc-300 bg-white hover:bg-zinc-50 py-3 text-sm transition-colors flex items-center justify-center gap-2.5"
              data-testid="google-auth-button"
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>

            {/* Sign in link */}
            <p className="text-center text-sm text-zinc-500 mt-6">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-zinc-900 font-medium hover:underline underline-offset-2"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}